import { listUsers } from "shared/api/admin/api";
import {
  APIListValuesRequest,
  APIListValuesResponse,
  APISuccessResponse,
  listManufactureLineExitsValues,
  listTransportCategories,
  listVehicleECUsValues,
  listVehicleOptionsValues,
  listVehiclePartsValues,
  listVehicleTagsValues,
  VehicleGenericCategory,
} from "shared/api/api";
import { SENSORS_LIMIT } from "shared/constants";
import { excludeNulls } from "shared/utils";

import { FilterGroupState } from "features/ui/Filters/FilterBuilder/types";
import {
  filterStateToFilterGroupState,
  getFiltersQuery,
  mergeFilterQueryStrings,
} from "features/ui/Filters/FilterBuilder/utils";
import { FilterOperator } from "features/ui/Filters/types";
import { SelectOption } from "features/ui/Select";

import { listClaimsValues } from "./claims/api";
import { listCustomRecordsValues } from "./customRecords/api";
import { listCustomSignalEventDefinitionValues } from "./customSignalEvents/hooks";
import {
  APIListValuesFailureModeEventRequest,
  APIListValuesVehicleRiskModelPredictionRequest,
  FailureMode,
  listFailureModeEventsValues,
  listFailureModeRiskModelPredictionsValues,
  listFailureModes,
  listFailureModesValues,
  listRiskModelPredictionsValues,
  listVehicleRiskModelPredictionsValues,
} from "./failureModes/api";
import { listInspectionsValues } from "./inspections/api";
import { listInstanceManagementTenantsValues } from "./instanceManagement/api";
import { listIssuesValues } from "./issues/api";
import { listPartsValues } from "./parts/api";
import { listGroupsValues } from "./rbac/api";
import { listRepairsValues } from "./repairs/api";
import { listInvestigationsValues } from "./schemaInvestigations/api";
import {
  getSensors,
  listSensorsReadingValues,
  listSensorValues,
} from "./sensors/api";
import { listServiceCentersValues } from "./serviceCenters/api";
import { listServiceRecordsValues } from "./serviceRecords/api";
import {
  listDealerValues,
  listSignalEventsOccurrencesValues,
  listSignalEventsValues,
} from "./signalEvents/api";
import { listSmartGroups, listSmartGroupsV2 } from "./smartGroups/api";
import type { ListSmartGroupsRequest, SmartGroup } from "./smartGroups/api";
import { listVectorClusters } from "./vectorClusters/api";
import { transformVectorClusterName } from "./vectorClusters/utils";
import { listVehiclesValues } from "./vehicles/api";
import {
  listWorkOrderPartsValues,
  listWorkOrdersValues,
} from "./workOrders/api";

export const vehicleLoadDataFunc = (args: APIListValuesRequest) =>
  listVehiclesValues(args).then(transformListValuesAPIResponseFunc);

export const vehicleTagsLoadDataFunc = (args: APIListValuesRequest) =>
  listVehicleTagsValues(args).then(transformListValuesAPIResponseFunc);

export const vehicleECUsLoadDataFunc = (args: APIListValuesRequest) =>
  listVehicleECUsValues(args).then(transformListValuesAPIResponseFunc);

export const vehicleOptionsLoadDataFunc = (args: APIListValuesRequest) =>
  listVehicleOptionsValues(args).then(transformListValuesAPIResponseFunc);

export const vehiclePartsLoadDataFunc = (args: APIListValuesRequest) =>
  listVehiclePartsValues(args).then(transformListValuesAPIResponseFunc);

export const manufactureLineExitsLoadDataFunc = (args: APIListValuesRequest) =>
  listManufactureLineExitsValues(args).then(transformListValuesAPIResponseFunc);

export const vectorClustersLoadDataFunc = (args: APIListValuesRequest) =>
  listVectorClusters({
    filter: getFiltersQuery(undefined, [
      {
        name: args.fieldName,
        op: "like",
        value: args.like,
      },
    ]),
  }).then(({ data }) =>
    data.map(
      (cluster) =>
        ({
          id: cluster.ID,
          value: transformVectorClusterName(cluster),
        }) as SelectOption
    )
  );

export const claimsLoadDataFunc = (args: APIListValuesRequest) =>
  listClaimsValues(args).then(transformListValuesAPIResponseFunc);

export const inspectionsLoadDataFunc = (args: APIListValuesRequest) =>
  listInspectionsValues(args).then(transformListValuesAPIResponseFunc);

export const customRecordsLoadDataFunc = (args: APIListValuesRequest) =>
  listCustomRecordsValues(args).then(transformListValuesAPIResponseFunc);

export const repairsLoadDataFunc = (args: APIListValuesRequest) =>
  listRepairsValues(args).then(transformListValuesAPIResponseFunc);

export const failureModesLoadDataFunc = (args: APIListValuesRequest) =>
  listFailureModesValues(args).then(transformListValuesAPIResponseFunc);

export const failureModeLoadDataFunc = () =>
  listFailureModes({}).then(transformFailureModeAPIResponseFunc);

export const signalEventsOccurrencesLoadDataFunc = (
  args: APIListValuesRequest
) =>
  listSignalEventsOccurrencesValues(args).then(
    transformListValuesAPIResponseFunc
  );

export const issuesLoadDataFunc = (args: APIListValuesRequest) =>
  listIssuesValues(args).then(transformListValuesAPIResponseFunc);

export const investigationsLoadDataFunc = (args: APIListValuesRequest) =>
  listInvestigationsValues(args).then(transformListValuesAPIResponseFunc);

export const partsLoadDataFunc = (args: APIListValuesRequest) =>
  listPartsValues(args).then(transformListValuesAPIResponseFunc);

export const transportCategoriesLoadDataFunc = (): Promise<SelectOption[]> =>
  listTransportCategories().then(transformTransportCategoryAPIResponseFunc);

export const groupsLoadDataFunc = (
  args: APIListValuesRequest
): Promise<SelectOption[]> =>
  listGroupsValues(args).then(transformListValuesAPIResponseFunc);

export const workOrderLoadDataFunc = (
  args: APIListValuesRequest
): Promise<SelectOption[]> =>
  listWorkOrdersValues(args).then(transformListValuesAPIResponseFunc);

export const workOrderPartLoadDataFunc = (
  args: APIListValuesRequest
): Promise<SelectOption[]> =>
  listWorkOrderPartsValues(args).then(transformListValuesAPIResponseFunc);

type ListSmartGroupsFunc = (
  args: ListSmartGroupsRequest
) => Promise<{ data: SmartGroup[] }>;

// Largest page the API allows. The filter shows every smart group, not the API's default page of 25.
const SMART_GROUPS_FILTER_LIMIT = 2000;

const makeSmartGroupsLoadDataFunc =
  (listFunc: ListSmartGroupsFunc, eventType?: string) =>
  async ({ filter, like }: APIListValuesRequest): Promise<SelectOption[]> => {
    const smartGroupsFilter = like
      ? getFiltersQuery(
          filterStateToFilterGroupState({
            name: {
              values: [like],
              operator: FilterOperator.CONTAINS,
            },
          })
        )
      : filter;

    const eventTypeFilter = eventType
      ? getFiltersQuery(
          filterStateToFilterGroupState({
            eventType: {
              values: [eventType],
              operator: FilterOperator.IN,
            },
          })
        )
      : undefined;

    const { data } = await listFunc({
      filter: eventTypeFilter
        ? mergeFilterQueryStrings(eventTypeFilter, smartGroupsFilter)
        : smartGroupsFilter,
      limit: SMART_GROUPS_FILTER_LIMIT,
      sort: "name:asc",
    });

    return data.map(
      ({ ID, name }) =>
        ({
          id: ID,
          value: name,
        }) as SelectOption
    );
  };

export const smartGroupsLoadDataFunc =
  makeSmartGroupsLoadDataFunc(listSmartGroups);

export const smartGroupsV2LoadDataFunc = (eventType: string) =>
  makeSmartGroupsLoadDataFunc(listSmartGroupsV2, eventType);

export const serviceRecordsLoadDataFunc = (
  args: APIListValuesRequest
): Promise<SelectOption[]> =>
  listServiceRecordsValues(args).then(transformListValuesAPIResponseFunc);

export const signalEventsValuesLoadDataFunc = (
  args: APIListValuesRequest
): Promise<SelectOption[]> =>
  listSignalEventsValues(args).then(transformListValuesAPIResponseFunc);

export const dealersLoadDataFunc = (args: APIListValuesRequest) =>
  listDealerValues(args).then(transformListValuesAPIResponseFunc);

export const customSignalEventDefinitionsLoadDataFunc = (
  args: APIListValuesRequest
) =>
  listCustomSignalEventDefinitionValues(args).then(
    transformListValuesAPIResponseFunc
  );

export const serviceCentersLoadDataFunc = (args: APIListValuesRequest) =>
  listServiceCentersValues(args).then(transformListValuesAPIResponseFunc);

export const failureModeEventLoadDataFunc = (
  args: APIListValuesFailureModeEventRequest
): Promise<SelectOption[]> =>
  listFailureModeEventsValues(args).then(transformListValuesAPIResponseFunc);

export const failureModeRiskModelPredictionLoadDataFunc = (
  args: APIListValuesFailureModeEventRequest
): Promise<SelectOption[]> =>
  listFailureModeRiskModelPredictionsValues(args).then(
    transformListValuesAPIResponseFunc
  );

export const vehicleRiskModelPredictionLoadDataFunc = (
  args: APIListValuesVehicleRiskModelPredictionRequest
): Promise<SelectOption[]> =>
  listVehicleRiskModelPredictionsValues(args).then(
    transformListValuesAPIResponseFunc
  );

export const riskModelPredictionLoadDataFunc = (
  args: APIListValuesRequest
): Promise<SelectOption[]> =>
  listRiskModelPredictionsValues(args).then(transformListValuesAPIResponseFunc);

const transformListValuesAPIResponseFunc = ({
  data: { distinctValues },
}: APISuccessResponse<APIListValuesResponse>): SelectOption[] => {
  if (!distinctValues) {
    return [];
  }

  return excludeNulls(distinctValues).map((v) => ({ id: v, value: v }));
};

const transformFailureModeAPIResponseFunc = ({
  data,
}: APISuccessResponse<FailureMode[]>): SelectOption[] => {
  if (!data) {
    return [];
  }

  return data.map(({ ID, name }) => ({ id: ID, value: name }));
};

const transformTransportCategoryAPIResponseFunc = ({
  data,
}: APISuccessResponse<VehicleGenericCategory[]>): SelectOption[] => {
  if (!data) {
    return [];
  }

  return data.map(({ ID, name }) => ({ id: ID, value: name }));
};

export const transformInitialSelectedVectorCluster = async (
  initialSelected: SelectOption[]
): Promise<SelectOption[]> => {
  const selectedIds = initialSelected.map(({ id }) => id.toString());
  const { data: clusters } = await listVectorClusters({
    filter: getFiltersQuery(undefined, [
      {
        name: "ID",
        op: "in",
        value: selectedIds,
      },
    ]),
    limit: selectedIds.length,
  });

  return Promise.all(
    initialSelected.map(async ({ id }) => {
      const cluster = clusters.find(({ ID }) => ID === id);

      return {
        id,
        value: cluster ? transformVectorClusterName(cluster) : id,
      };
    })
  );
};

const makeTransformInitialSelectedSmartGroup =
  (listFunc: ListSmartGroupsFunc) =>
  async (initialSelected: SelectOption[]): Promise<SelectOption[]> => {
    const filter: FilterGroupState = {
      type: "group",
      id: "group-1",
      anyAll: "all",
      children: [
        {
          type: "row",
          id: "row-1",
          attribute: "ID",
          operator: FilterOperator.IN,
          values: initialSelected.map(({ id }) => id.toString()),
        },
      ],
    };

    const { data: smartGroups } = await listFunc({
      filter: getFiltersQuery(filter),
    });

    return initialSelected.map(({ id }) => ({
      id,
      value: smartGroups.find(({ ID }) => ID === id)?.name || "",
    }));
  };

export const transformInitialSelectedSmartGroup =
  makeTransformInitialSelectedSmartGroup(listSmartGroups);

export const transformInitialSelectedSmartGroupV2 =
  makeTransformInitialSelectedSmartGroup(listSmartGroupsV2);

export const transformInitialSelectedFailureMode = async (
  initialSelected: SelectOption[]
): Promise<SelectOption[]> => {
  const { data: failureModes } = await listFailureModes({});

  return Promise.all(
    initialSelected.map(async ({ id }) => ({
      id,
      value: failureModes.find((fm) => fm.ID === id)?.name || "",
    }))
  );
};

export const transformInitialSelectedTransportCategory = async (
  initialSelected: SelectOption[]
): Promise<SelectOption[]> => {
  const { data: failureModes } = await listTransportCategories();

  return Promise.all(
    initialSelected.map(async ({ id }) => ({
      id,
      value: failureModes.find((tc) => tc.ID === id)?.name || "",
    }))
  );
};

export const loadSensorsFunc = async (
  args: APIListValuesRequest
): Promise<SelectOption[]> =>
  getSensors({
    limit: SENSORS_LIMIT,
    ...args,
  }).then(({ data }) =>
    data.map(
      ({ ID, displayName }) =>
        ({
          id: ID,
          value: displayName,
        }) as SelectOption
    )
  );

// loading sensor values requires some additional magic since /values endpoint for sensors
// has some additional validation implemented that is not required here
export const loadSensorValuesFunc = async (
  args: APIListValuesRequest
): Promise<SelectOption[]> =>
  listSensorValues(args).then(transformListValuesAPIResponseFunc);

export const loadTenantsFunc = async (
  args: APIListValuesRequest
): Promise<SelectOption[]> =>
  listInstanceManagementTenantsValues(args).then(
    transformListValuesAPIResponseFunc
  );

export const loadSensorReadingsValuesFunc = async (
  args: APIListValuesRequest
): Promise<SelectOption[]> =>
  listSensorsReadingValues(args).then(({ data: { distinctValues } }) =>
    distinctValues.map(
      (value) =>
        ({
          id: value,
          value,
        }) as SelectOption
    )
  );

export const transformInitialSelectedSensors = async (
  initialSelected: SelectOption[]
): Promise<SelectOption[]> => {
  const { data: sensors } = await getSensors({
    filter: getFiltersQuery(
      filterStateToFilterGroupState({
        ID: {
          operator: FilterOperator.IN,
          values: initialSelected.map(({ id }) => id.toString()),
        },
      })
    ),
  });

  return Promise.all(
    initialSelected.map(async ({ id }) => ({
      id,
      value: sensors.find(({ ID }) => ID === id)?.displayName || "",
    }))
  );
};

export const usersLoadDataFunc = async (): Promise<SelectOption[]> =>
  listUsers().then(({ data }) =>
    data.map(
      ({ email }) =>
        ({
          id: email,
          value: email,
        }) as SelectOption
    )
  );
